import type { ExchangeAccount, FreePeriodPackage, StrapiId } from "@investhinks/api-client";
import moment from "moment";
import React, { type ChangeEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";

import { subscriptionsEndpoints } from "../../api";
import getErrorMessage from "../../utils/getErrorMessage";

interface EditFreePeriodModalProps {
  isOpen: boolean;
  toggle: () => void;
  exchangeAccount: ExchangeAccount | null;
  onSuccess?: () => void;
}

const EditFreePeriodModal: React.FC<EditFreePeriodModalProps> = ({ isOpen, toggle, exchangeAccount, onSuccess }) => {
  const { t } = useTranslation("users", { keyPrefix: "freePeriods" });
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [packages, setPackages] = useState<FreePeriodPackage[]>([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeactivateId, setPendingDeactivateId] = useState<StrapiId | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);

  const exchangeAccountId = exchangeAccount?.id;
  const displayName = exchangeAccount?.name || `Account #${exchangeAccountId}`;

  const loadPackages = useCallback(async () => {
    if (!exchangeAccountId) return;
    setIsLoadingPackages(true);
    try {
      const { data } = await subscriptionsEndpoints.getPackagesByCredentials(exchangeAccountId);
      setPackages(data || []);
    } catch (err: unknown) {
      console.error("Failed to load packages:", err);
      setPackages([]);
    } finally {
      setIsLoadingPackages(false);
    }
  }, [exchangeAccountId]);

  useEffect(() => {
    if (isOpen && exchangeAccountId) {
      setStartDate(moment().format("YYYY-MM-DD"));
      setEndDate(null);
      setReason("");
      setError(null);
      setPendingDeactivateId(null);
      loadPackages();
    }
  }, [isOpen, exchangeAccountId, loadPackages]);

  const handleQuickAction = (months: number) => {
    const newEndDate = moment(startDate).add(months, "months");
    setEndDate(newEndDate.format("YYYY-MM-DD"));
  };

  const handleStartDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    // If end date is before new start date, clear it
    if (endDate && moment(e.target.value).isAfter(moment(endDate))) {
      setEndDate(null);
    }
  };

  const handleEndDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  const handleSave = async () => {
    if (!startDate || !endDate || !exchangeAccountId) return;

    const start = moment(startDate);
    const end = moment(endDate);

    if (end.isSameOrBefore(start)) {
      setError(t("errorEndMustBeAfterStart"));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await subscriptionsEndpoints.createFreePeriodPackage({
        exchange_account: exchangeAccountId,
        startDate: start.toISOString(),
        endDate: end.endOf("day").toISOString(),
        reason: reason || undefined,
        grantType: "admin",
      });
      onSuccess?.();
    } catch (err: unknown) {
      console.error("Failed to create free period package:", err);
      setError(getErrorMessage(err, t("errorGeneric")));
    } finally {
      setIsLoading(false);
    }
  };

  // Free-period packages decide whether a user is billed, so a double-click
  // must not issue two deactivations. Every other mutation here guards; this
  // one did not.
  const handleDeactivate = async (packageId: StrapiId) => {
    if (isDeactivating) return;
    setIsDeactivating(true);
    try {
      await subscriptionsEndpoints.deactivatePackage(packageId);
      setPendingDeactivateId(null);
      loadPackages();
    } catch (err: unknown) {
      console.error("Failed to deactivate package:", err);
      setError(getErrorMessage(err, t("errorDeactivate")));
      setPendingDeactivateId(null);
    } finally {
      setIsDeactivating(false);
    }
  };

  const getDaysCount = (): number => {
    if (!startDate || !endDate) return 0;
    return Math.max(0, moment(endDate).diff(moment(startDate), "days"));
  };

  const getActivePackages = (): FreePeriodPackage[] => {
    return packages.filter((pkg) => {
      return pkg.isActive && moment(pkg.endDate).isAfter(moment());
    });
  };

  const getTotalFreeDaysRemaining = (): number => {
    const activePackages = getActivePackages();
    if (activePackages.length === 0) return 0;

    const now = moment();

    // Get all date ranges, clipping start to now if in the past
    const ranges = activePackages
      .map((pkg) => {
        const start = moment(pkg.startDate);
        const end = moment(pkg.endDate);
        return {
          start: start.isAfter(now) ? start : now.clone(),
          end: end,
        };
      })
      .filter((r) => r.end.isAfter(r.start));

    if (ranges.length === 0) return 0;

    // Sort by start date
    ranges.sort((a, b) => a.start.valueOf() - b.start.valueOf());

    // Merge overlapping ranges
    const merged = [{ ...ranges[0] }];
    for (let i = 1; i < ranges.length; i++) {
      const last = merged[merged.length - 1];
      const current = ranges[i];
      if (current.start.isSameOrBefore(last.end)) {
        // Overlapping - extend end if needed
        if (current.end.isAfter(last.end)) {
          last.end = current.end;
        }
      } else {
        // Non-overlapping - add new range
        merged.push({ ...current });
      }
    }

    // Sum days from merged ranges
    return merged.reduce((sum, r) => sum + Math.max(0, r.end.diff(r.start, "days")), 0);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} style={styles.modalHeader}>
        <div style={styles.headerContent}>
          <i className="fas fa-calendar-plus" style={styles.headerIcon} />
          <span>{t("editTitle")}</span>
        </div>
      </ModalHeader>

      <ModalBody style={styles.modalBody}>
        {/* Exchange Account Info */}
        <div style={styles.accountInfo}>
          <div style={styles.accountName}>{displayName}</div>
          <Badge color="light" style={styles.accountId}>
            #{exchangeAccountId}
          </Badge>
        </div>

        {/* Current Status */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{t("currentStatus")}</div>
          <div style={styles.statusCard}>
            <div style={styles.statusRow}>
              <span style={styles.statusLabel}>{t("activePackages")}:</span>
              <span style={styles.statusValue}>{getActivePackages().length}</span>
            </div>
            <div style={styles.statusRow}>
              <span style={styles.statusLabel}>{t("totalFreeDays")}:</span>
              <span style={styles.statusValueHighlight}>
                {getTotalFreeDaysRemaining()} {t("days")}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{t("quickActions")}</div>
          <div style={styles.quickActionsGrid}>
            {[1, 2, 3, 6].map((months) => (
              <button
                key={months}
                onClick={() => handleQuickAction(months)}
                style={{
                  ...styles.quickActionButton,
                  ...(endDate === moment(startDate).add(months, "months").format("YYYY-MM-DD")
                    ? styles.quickActionButtonActive
                    : {}),
                }}
              >
                <span style={styles.quickActionPlus}>+</span>
                <span style={styles.quickActionNumber}>{months}</span>
                <span style={styles.quickActionLabel}>{months === 1 ? t("month") : t("months")}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Date Pickers */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{t("dateRange")}</div>
          <div style={styles.datePickersRow}>
            <div style={styles.datePickerGroup}>
              <label style={styles.dateLabel}>{t("startDate")}</label>
              <Input type="date" value={startDate || ""} onChange={handleStartDateChange} style={styles.dateInput} />
            </div>
            <div style={styles.datePickerGroup}>
              <label style={styles.dateLabel}>{t("endDate")}</label>
              <Input
                type="date"
                value={endDate || ""}
                onChange={handleEndDateChange}
                min={startDate ? moment(startDate).add(1, "day").format("YYYY-MM-DD") : undefined}
                style={styles.dateInput}
              />
            </div>
          </div>
          {startDate && endDate && (
            <div style={styles.periodPreview}>
              <i className="fas fa-calendar-check" style={styles.previewIcon} />
              <span>{t("periodLength")}: </span>
              <strong>
                {getDaysCount()} {t("days")}
              </strong>
              <span style={styles.periodDates}>
                ({moment(startDate).format("DD MMM YYYY")} - {moment(endDate).format("DD MMM YYYY")})
              </span>
            </div>
          )}
        </div>

        {/* Reason */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{t("reason")}</div>
          <Input
            type="textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("reasonPlaceholder")}
            rows={2}
            style={styles.reasonInput}
          />
        </div>

        {/* Error */}
        {error && (
          <div style={styles.errorBanner}>
            <i className="fas fa-exclamation-circle" style={styles.errorIcon} />
            <span>{error}</span>
          </div>
        )}

        {/* Active Packages */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{t("activePackages")}</div>
          <div style={styles.packagesContainer}>
            {isLoadingPackages ? (
              <div style={styles.packagesLoading}>
                <Spinner size="sm" color="secondary" />
              </div>
            ) : getActivePackages().length === 0 ? (
              <div style={styles.packagesEmpty}>{t("noPackages")}</div>
            ) : (
              getActivePackages().map((pkg) => {
                const pkgId = pkg.id;
                const startMoment = moment(pkg.startDate);
                const endMoment = moment(pkg.endDate);
                const daysRemaining = Math.max(0, endMoment.diff(moment(), "days"));

                return (
                  <div key={pkgId} style={styles.packageItem}>
                    <div style={styles.packageHeader}>
                      <div style={styles.packageDates}>
                        <span style={styles.packageDate}>{startMoment.format("DD MMM YYYY")}</span>
                        <i className="fas fa-arrow-right" style={styles.packageArrow} />
                        <span style={styles.packageDate}>{endMoment.format("DD MMM YYYY")}</span>
                      </div>
                      <Badge color="success" style={styles.packageDaysBadge}>
                        {daysRemaining} {t("days")}
                      </Badge>
                    </div>
                    <div style={styles.packageDetails}>
                      <span style={styles.packageType}>{t(`grantType.${pkg.grantType}`) || pkg.grantType}</span>
                      {pkg.reason && <span style={styles.packageReason}>- {pkg.reason}</span>}
                      {pendingDeactivateId === pkgId ? (
                        <div style={styles.confirmButtons}>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleDeactivate(pkgId)}
                            disabled={isDeactivating}
                            style={styles.confirmYes}
                          >
                            {isDeactivating && <Spinner size="sm" className="mr-2" />}
                            {t("confirmRemove")}
                          </Button>
                          <Button
                            color="secondary"
                            size="sm"
                            outline
                            onClick={() => setPendingDeactivateId(null)}
                            disabled={isDeactivating}
                            style={styles.confirmNo}
                          >
                            {t("cancel")}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          color="link"
                          size="sm"
                          onClick={() => setPendingDeactivateId(pkgId)}
                          style={styles.deactivateButton}
                          title={t("deactivatePackage")}
                          aria-label={t("deactivatePackage")}
                        >
                          <i className="fas fa-times" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter style={styles.modalFooter}>
        <Button color="secondary" outline onClick={toggle} disabled={isLoading}>
          {t("cancel")}
        </Button>
        <Button
          color="primary"
          onClick={handleSave}
          disabled={!startDate || !endDate || isLoading}
          style={styles.saveButton}
        >
          {isLoading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              {t("saving")}
            </>
          ) : (
            <>
              <i className="fas fa-plus mr-2" />
              {t("createPackage")}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const styles = {
  modalHeader: {
    borderBottom: "1px solid #e5e7eb",
    padding: "16px 20px",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  headerIcon: {
    color: "#3b82f6",
    fontSize: 18,
  },
  modalBody: {
    padding: "20px",
    maxHeight: "70vh",
    overflowY: "auto",
  },
  accountInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: "1px solid #e5e7eb",
  },
  accountName: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
  },
  accountId: {
    fontSize: 11,
    fontWeight: 500,
    color: "#6b7280",
    backgroundColor: "#f3f4f6",
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 10,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statusCard: {
    backgroundColor: "#f9fafb",
    borderRadius: 8,
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  statusRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  statusLabel: {
    fontSize: 13,
    color: "#6b7280",
  },
  statusValue: {
    fontSize: 14,
    fontWeight: 500,
    color: "#111827",
  },
  statusValueHighlight: {
    fontSize: 14,
    fontWeight: 600,
    color: "#10b981",
  },
  quickActionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(72px, 1fr))",
    gap: 10,
  },
  quickActionButton: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px 8px",
    backgroundColor: "#f3f4f6",
    border: "2px solid transparent",
    borderRadius: 10,
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  quickActionButtonActive: {
    backgroundColor: "#eff6ff",
    borderColor: "#3b82f6",
  },
  quickActionPlus: {
    fontSize: 14,
    fontWeight: 400,
    color: "#9ca3af",
    marginBottom: -4,
  },
  quickActionNumber: {
    fontSize: 24,
    fontWeight: 700,
    color: "#111827",
  },
  quickActionLabel: {
    fontSize: 11,
    color: "#6b7280",
    textTransform: "uppercase",
    marginTop: -2,
  },
  datePickersRow: {
    display: "flex",
    gap: 16,
  },
  datePickerGroup: {
    flex: 1,
  },
  dateLabel: {
    display: "block",
    fontSize: 12,
    color: "#6b7280",
    marginBottom: 4,
  },
  dateInput: {
    padding: "10px 12px",
    fontSize: 14,
    borderRadius: 8,
    border: "1px solid #d1d5db",
  },
  periodPreview: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
    padding: "10px 14px",
    backgroundColor: "#ecfdf5",
    borderRadius: 8,
    fontSize: 13,
    color: "#065f46",
  },
  previewIcon: {
    color: "#10b981",
  },
  periodDates: {
    marginLeft: "auto",
    color: "#6b7280",
    fontSize: 12,
  },
  reasonInput: {
    padding: "10px 12px",
    fontSize: 14,
    borderRadius: 8,
    border: "1px solid #d1d5db",
    resize: "none",
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    backgroundColor: "#fef2f2",
    borderRadius: 8,
    fontSize: 13,
    color: "#991b1b",
    marginBottom: 20,
  },
  errorIcon: {
    color: "#ef4444",
  },
  packagesContainer: {
    backgroundColor: "#f9fafb",
    borderRadius: 8,
    padding: 12,
    maxHeight: 200,
    overflowY: "auto",
  },
  packagesLoading: {
    display: "flex",
    justifyContent: "center",
    padding: 16,
  },
  packagesEmpty: {
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 13,
    padding: 16,
  },
  packageItem: {
    padding: "10px 0",
    borderBottom: "1px solid #e5e7eb",
  },
  packageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  packageDates: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
  },
  packageDate: {
    fontWeight: 500,
    color: "#111827",
  },
  packageArrow: {
    fontSize: 10,
    color: "#9ca3af",
  },
  packageDaysBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 8px",
  },
  packageDetails: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
  },
  packageType: {
    color: "#6b7280",
    textTransform: "capitalize",
  },
  packageReason: {
    color: "#9ca3af",
    fontStyle: "italic",
    flex: 1,
  },
  deactivateButton: {
    padding: "2px 6px",
    fontSize: 12,
    color: "#ef4444",
    marginLeft: "auto",
  },
  confirmButtons: {
    display: "flex",
    gap: 6,
    marginLeft: "auto",
  },
  confirmYes: {
    padding: "2px 8px",
    fontSize: 11,
  },
  confirmNo: {
    padding: "2px 8px",
    fontSize: 11,
  },
  modalFooter: {
    borderTop: "1px solid #e5e7eb",
    padding: "14px 20px",
    display: "flex",
    // A minWidth-pinned primary button plus its cancel overflowed a 360px
    // footer once the label was translated.
    flexWrap: "wrap" as const,
    justifyContent: "flex-end",
    gap: 10,
  },
  saveButton: {
    minWidth: 140,
  },
} as const;

export default EditFreePeriodModal;
